"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick, expansionClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Structure 1064 — KUK Programme Structure.
 *
 * Port of kuk-campus-portal `components/Degree/ProgramStructure.tsx`: a band of fixed
 * background art carrying the heading and a Brochure CTA on the left and a
 * single-open semester accordion of subject tables on the right, with an optional
 * rich-text disclaimer beneath.
 *
 * DATA — the same flat metadata rows Program Structure 1042 stores, so nothing new is
 * kept: `semester_name` groups a row into a section and `subject_name` is its line;
 * `semester_is_elective_based` marks the whole semester "as per elective", which
 * renders the pill beside its label. v1's `credits` columns are commented out in the
 * reference markup, so the field is stored and simply not drawn — uncommenting it
 * later needs no migration.
 *
 * ONE OPEN AT A TIME, AND NEVER NONE. v1 uses `type="single" collapsible={false}` with
 * `defaultValue="item-1"`, so the first semester starts open and clicking the open
 * header does not close it. Reproduced exactly, including the "cannot close the last
 * one" part, which is easy to lose when porting an accordion.
 *
 * THE BACKGROUND IS FIXED — `background-attachment: fixed` in the reference, so the
 * art parallaxes as the page scrolls past. Kept, with the image as a CMS field.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * THE DISCLAIMER IS DRAWN ONCE. v1 renders it twice — a desktop-only copy inside the
 * right column and a mobile-only copy after the CTA — which duplicates the same HTML
 * into the DOM at every width. One block that moves with the flex direction reads the
 * same and cannot drift.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch (download straight through vs open
 * the sign-up form) and its `getPath()` guard, which hides the CTA on a hardcoded list
 * of routes. Both are per-visitor or per-route state, not page content: an editor who
 * wants a direct link turns `brochure_cta_is_cta` off and sets the URL, and one with
 * no brochure clears the label.
 */

/** The 12x12 chevron the reference renders on the accordion trigger. */
const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className={cn("shrink-0 transition-transform", open && "rotate-180")}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used on
 * 12 headings across the reference. Defined in ITS globals.css as a `::after` with
 * `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can come
 * from a CMS field (a pseudo-element cannot read an inline style). `w-fit` on the
 * heading is load-bearing: the rule is 33% of the HEADING, and a full-width h2 would
 * stretch it across a third of the page.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

export interface ProgramStructure1064Row {
  id?: string | number;
  semester_name?: string;
  /** Marks the whole semester as "as per elective". */
  semester_is_elective_based?: boolean;
  subject_name?: string;
  credits?: string | number;
  priority?: number;
  status?: boolean;
}

interface ProgramStructure1064Data {
  metadata?: ProgramStructure1064Row[];
  title?: string;
  title_text_color?: string;
  /** Fixed background art. */
  section_background_image?: string;
  section_background_image_alt?: string;
  /** Fill behind the art (and the whole band when there is no art). */
  section_background_color?: string;
  /** Subject card surface. */
  subject_container_color?: string;
  /** Subject table header rule + label. */
  header_section_color?: string;
  subjects_column_label?: string;
  /** "As per elective" pill. */
  electives_tab_label?: string;
  card_background_color?: string;
  /** Rich-text disclaimer under the accordion. */
  section_description?: string;
  brochure_cta_text?: string;
  brochure_cta_text_color?: string;
  brochure_cta_border_color?: string;
  cta_button_color?: string;
  cta_button_icon?: string;
  /** Off → link straight to `cta_button_text` instead of opening the lead form. */
  brochure_cta_is_cta?: boolean;
  cta_button_text?: string;
  /** Accent rule under the heading (v1's `.custom-underline`). */
  gradient_color_1?: string;
  status?: boolean | number | string;
}

interface SemesterGroup {
  name: string;
  priority: number;
  isElectiveBased: boolean;
  subjects: string[];
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** v1 treats a disclaimer of exactly `<p><br></p>` as empty. */
function hasDisclaimer(html: string | undefined): boolean {
  if (!html) return false;
  const trimmed = html.trim();
  return trimmed !== "" && trimmed !== "<p><br></p>";
}

const ProgramStructure1064 = (props: { data?: ProgramStructure1064Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramStructure1064Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "Programme Structure",
    title_text_color = "#101112",
    section_background_image = "",
    section_background_color = "#F5F5F5",
    subject_container_color = "#FFFFFF",
    header_section_color = "#9F1C33",
    subjects_column_label = "Subject",
    electives_tab_label = "As per elective",
    card_background_color = "rgba(159, 28, 51, 0.2)",
    section_description = "",
    brochure_cta_text = "Brochure",
    brochure_cta_text_color = "#FFFFFF",
    cta_button_color = "#9F1C33",
    cta_button_icon = "download",
    cta_button_text = "",
    gradient_color_1 = "#9F1C33",
  } = sectionData || {};

  const brochureIsCta = isCmsShowFlagOn(sectionData?.brochure_cta_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const semesters = useMemo<SemesterGroup[]>(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    const order: string[] = [];
    const byName = new Map<string, SemesterGroup>();
    rows
      .filter((r) => r?.status !== false && !!r?.semester_name)
      .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0))
      .forEach((row, index) => {
        const name = String(row.semester_name);
        if (!byName.has(name)) {
          order.push(name);
          byName.set(name, {
            name,
            priority: Number(row.priority) || index,
            isElectiveBased: false,
            subjects: [],
          });
        }
        const group = byName.get(name)!;
        // Any row may carry the flag; one is enough to mark the semester.
        if (row.semester_is_elective_based) group.isElectiveBased = true;
        if (row.subject_name) group.subjects.push(row.subject_name);
      });
    return order.map((name) => byName.get(name)!);
  }, [sectionData?.metadata]);

  const [openIndex, setOpenIndex] = useState(0);

  // A shorter row set (CMS edit / preview) can leave `openIndex` past the end.
  useEffect(() => {
    if (semesters.length > 0 && openIndex > semesters.length - 1) setOpenIndex(0);
  }, [semesters.length, openIndex]);

  const handleBrochureClick = useCallback(() => {
    ctaClick({
      section: "program_structure_1064",
      component: "download_brochure",
      label: brochure_cta_text?.trim() || "brochure",
      step: "1",
      entity: "program_structure_1064",
      parentEntity: "program_structure_1064",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.DOWNLOAD }));
  }, [brochure_cta_text, dispatch]);

  if (!status || semesters.length === 0) return null;

  const ctaClassName = cn(
    "inline-flex h-[40px] items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md px-6 text-sm font-medium shadow transition-colors",
  );
  const ctaStyle = {
    backgroundColor: cta_button_color,
    color: brochure_cta_text_color,
  };
  const ctaInner = (
    <>
      {cta_button_icon ? (
        <DynamicIcon
          iconName={cta_button_icon}
          width="16"
          height="16"
          color={brochure_cta_text_color}
        />
      ) : null}
      <span>{brochure_cta_text}</span>
    </>
  );
  const renderCta = (className: string) =>
    brochure_cta_text ? (
      brochureIsCta || !cta_button_text ? (
        <button
          type="button"
          onClick={handleBrochureClick}
          className={cn(ctaClassName, className)}
          style={ctaStyle}
        >
          {ctaInner}
        </button>
      ) : (
        <Link
          href={normalizeHref(cta_button_text)}
          className={cn(ctaClassName, className)}
          style={ctaStyle}
        >
          {ctaInner}
        </Link>
      )
    ) : null;

  return (
    <section
      className="w-full bg-cover bg-center bg-no-repeat"
      style={{
        backgroundColor: section_background_color,
        ...(section_background_image
          ? {
              backgroundImage: `url('${section_background_image}')`,
              backgroundAttachment: "fixed",
            }
          : {}),
      }}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-[1440px] flex-col items-start justify-between",
          "p-5 tab:flex-row tab:p-20",
        )}
      >
        <div className="tab:w-[30%] tab:space-y-5">
          {title ? (
            <h2
              className="relative w-fit text-2xl font-bold leading-[45px] tab:text-4xl"
              style={{ color: title_text_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: gradient_color_1 }}
              />
            </h2>
          ) : null}
          {renderCta("hidden w-[152px] tab:inline-flex")}
        </div>

        <div className="w-full space-y-5 py-10 tab:w-[70%] tab:p-10 tab:py-0">
          {semesters.map((semester, index) => {
            const open = openIndex === index;
            return (
              <div key={semester.name}>
                <button
                  type="button"
                  // v1 sets `collapsible={false}`: the open header does not close.
                  onClick={() => {
                    if (open) return;
                    setOpenIndex(index);
                    expansionClick({
                      section: "programme_structure",
                      component: "icon_expansion",
                      label: semester.name,
                    });
                  }}
                  aria-expanded={open}
                  className={cn(
                    "flex w-full items-center justify-start gap-2 p-5 text-left",
                    "text-base font-medium",
                  )}
                  style={{ color: title_text_color }}
                >
                  <span>{semester.name}</span>
                  {semester.isElectiveBased && electives_tab_label ? (
                    <span
                      className="rounded-full p-2 text-sm font-light"
                      style={{ backgroundColor: card_background_color }}
                    >
                      {electives_tab_label}
                    </span>
                  ) : null}
                  <ChevronDown open={open} />
                </button>

                {open && semester.subjects.length > 0 ? (
                  <div
                    className="mb-2 rounded-xl p-5 shadow-2xl"
                    style={{ backgroundColor: subject_container_color }}
                  >
                    <table className="w-full text-left">
                      <thead>
                        <tr
                          className="border-b-2"
                          style={{ borderColor: `${header_section_color}33` }}
                        >
                          <th
                            className="pb-2 text-sm font-medium"
                            style={{ color: title_text_color }}
                          >
                            {subjects_column_label}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {semester.subjects.map((subject, i) => (
                          <tr key={`${subject}-${i}`}>
                            <td
                              className="w-full py-2 text-sm font-medium"
                              style={{ color: title_text_color }}
                            >
                              {subject}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            );
          })}

          {renderCta("mt-2 flex w-full tab:hidden")}

          {hasDisclaimer(section_description) ? (
            <div
              className={cn(
                "pt-10 text-center text-sm",
                "[&_a]:text-inherit [&_a]:underline",
              )}
              style={{ color: title_text_color }}
              dangerouslySetInnerHTML={{ __html: section_description }}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ProgramStructure1064;
